import { DeleteModal } from '@/src/components/common/Modal/DeleteModal'
import { ReferralSlip } from '@/src/components/common/ReferralSlip'
import { useFacilityDisplayName } from '@/src/components/common/ReferralSlip/useFacilityDisplayName'
import { createScopedPrint } from '@/src/utils/printScoped'
import { src } from '@data'
import {
  useBundleImagingResults,
  useImagingOrder,
  useImagingResult,
  useMarkNotificationForNotifiable,
  useOrder,
  useUploadFile
} from '@hook'
import { DeleteIcon, PrintIcon } from '@icons'
import { DOCTOR_MODAL_TYPE, useModalStore } from '@modal'
import { useAppDispatch } from '@store/hooks'
import { showViewImagingResultFile } from '@store/slicers/ModalSlicer'
import { OrderStatus } from '@type/imaging.type'
import type { ImagingDataMock, ImagingOrderData, ImagingOrderIncludedPatient } from '@type/imaging_order.type'
import { ImagingResultAttachmentIncluded } from '@type/imaging_results.type'
import { ActiveTab, Badge, Button, Loader, SideDrawer } from '@ui'
import ResultUploader from '@ui/FileUploader/results'
import { getError, getFileIconSrc, getIncludedData, guardEncounterWrite, resolveApiAssetUrl, showToast } from '@util'
import { Avatar, Box, CheckBox, Text, Tip } from 'grommet'
import parse from 'html-react-parser'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import ViewImagingResults from '../../../../PDirectory/Result/Modals/viewImagingResults'
import {
  buildImagingPlacedLines,
  createImagingResultAttachmentPayload,
  formatAttachmentSize,
  getImagingIncluded,
  imagingResultListLabel,
  imagingResultRowLabel,
  orderTitleNumber,
  outsideImagingLines
} from './ImagingDetails.helpers'

const TABS = ['Order Details', 'Result'] as const

const referralPrint = createScopedPrint('imaging-referral')

export type ImagingDetailsModalProps = {
  isOpen: boolean
  onClose: () => void
  id: string
  /** Row from the encounter table; used until `GET /imaging_orders/:id` resolves (and for legacy `order` rows). */
  defaultData?: ImagingDataMock
}

const isImagingOrderBundle = (row?: ImagingDataMock): row is ImagingOrderData => row?.type === 'imaging_order'

/** One saved result, normalised across bundle `imaging_result` rows and legacy `meta.result_attachments`. */
type ResultEntry = {
  attachment?: ImagingResultAttachmentIncluded | null
  key: string
  label: string
  resultId?: string
}

const ImagingDetails = ({ isOpen, onClose, id, defaultData: rowFromTable }: ImagingDetailsModalProps) => {
  useMarkNotificationForNotifiable('ImagingOrder', isOpen ? id : null)
  const dispatch = useAppDispatch()
  const { openModal } = useModalStore()
  const { patientId = '', encounterId = '' } = useParams<{ patientId: string; encounterId: string }>()

  const [activeTab, setActiveTab] = useState<(typeof TABS)[number]>('Order Details')
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [isCompleting, setIsCompleting] = useState(false)
  const [resultToDelete, setResultToDelete] = useState<ResultEntry | null>(null)
  const [printReferral, setPrintReferral] = useState(false)
  const facilityName = useFacilityDisplayName()

  // Leaving the drawer mid-print must not leave the app stuck in print mode.
  useEffect(() => () => referralPrint.end(), [])

  const {
    completeImagingOrder,
    createImagingOrderResult,
    included: listIncluded,
    singleImagingOrder,
    singleImagingOrderIsLoading
  } = useImagingOrder({ encounterId, id, patientId })
  const { uploadFile, isUploading } = useUploadFile()
  const { updateOrderStatus } = useOrder()
  const { deleteImagingResult, isDeletingImagingResult } = useImagingResult()

  const orderData = useMemo((): ImagingDataMock | undefined => {
    const fromApi = singleImagingOrder?.data
    if (fromApi?.type === 'imaging_order') return fromApi
    return rowFromTable
  }, [rowFromTable, singleImagingOrder?.data])

  const included = singleImagingOrder?.included ?? listIncluded
  const isBundle = isImagingOrderBundle(orderData)

  // Legacy single-line `order` rows keep their result on a separate resource.
  const legacyResultId = !isBundle ? orderData?.relationships?.imaging_result?.data?.id : undefined
  const { imagingResult, isImagingResultLoading } = useImagingResult(
    !isBundle ? orderData?.id : undefined,
    legacyResultId
  )

  useEffect(() => {
    setActiveTab('Order Details')
    setSelectedKeys(new Set())
  }, [id])

  const statusLabel = !orderData
    ? '-'
    : isImagingOrderBundle(orderData)
      ? orderData.attributes.status || '-'
      : orderData.meta?.status || '-'
  const isCompleteOrCancelled = ['completed', 'cancelled', 'canceled'].includes(statusLabel.toLowerCase())

  const placedLines = useMemo(() => buildImagingPlacedLines(orderData, included), [orderData, included])
  const lineKeys = useMemo(() => placedLines.map(line => line.key), [placedLines])
  const allSelected = lineKeys.length > 0 && lineKeys.every(key => selectedKeys.has(key))
  /** Studies done at an outside centre: printed on the referral slip, resulted by upload. */
  const outsideLines = useMemo(() => outsideImagingLines(placedLines), [placedLines])

  const imaging = getImagingIncluded(included, !isBundle ? orderData?.relationships.orderable.data?.id : undefined)

  const patientRow = orderData
    ? (getIncludedData(included, 'patient', orderData.relationships.patient.data?.id) as
        | ImagingOrderIncludedPatient
        | undefined)
    : undefined

  const staffRow = orderData
    ? (getIncludedData(included, 'staff', orderData.relationships.ordered_by.data?.id) as
        | { attributes?: { first_name?: string; last_name?: string }; meta?: { roles?: string[] } }
        | undefined)
    : undefined

  /** Studies can only be edited before the first upload. */
  const isStudyNamesEditable = isImagingOrderBundle(orderData) ? orderData.attributes.study_names_editable : false

  const { imagingResults: bundleImagingResults } = useBundleImagingResults(isBundle ? orderData : undefined, included)

  const resultEntries = useMemo((): ResultEntry[] => {
    if (isBundle) {
      return bundleImagingResults.map(result => ({
        attachment: result.attributes.attachment,
        key: result.id,
        label: imagingResultRowLabel(result),
        resultId: result.id
      }))
    }
    return (imagingResult?.meta?.result_attachments ?? []).map((attachment, idx) => ({
      attachment,
      key: String(attachment.id || idx),
      label: imagingResultListLabel(attachment, idx)
    }))
  }, [bundleImagingResults, imagingResult, isBundle])

  /** Studies resulted by file upload — free text and outside lines — still awaiting a file. */
  const uploadableStudies = useMemo(() => {
    const uploadedNames = new Set(
      bundleImagingResults.map(result => result.attributes.study_name?.trim().toLowerCase()).filter(Boolean) as string[]
    )
    return placedLines.filter(line => {
      const status = line.lineStatus.toLowerCase()
      if (['cancelled', 'canceled', 'completed'].includes(status)) return false
      if (line.orderId && line.performedAt !== 'outside') return false
      return !uploadedNames.has(line.label.trim().toLowerCase())
    })
  }, [bundleImagingResults, placedLines])

  const orderedByLabel = staffRow?.attributes?.first_name
    ? `${staffRow.meta?.roles?.includes('doctor') ? 'Dr. ' : ''}${staffRow.attributes.first_name} ${staffRow.attributes.last_name ?? ''}`.trim()
    : '-'

  const handlePrintReferral = useCallback(() => {
    if (!outsideLines.length) {
      showToast('No outside studies on this order')
      return
    }
    setPrintReferral(true)
    setTimeout(() => referralPrint.print(), 150)
  }, [outsideLines.length])

  const detailRows = useMemo(() => {
    if (!orderData) {
      return {
        Comments: '-',
        Radiology: '-',
        'Order by': (
          <span className="ml-auto flex items-center gap-2">
            <Avatar size="24px" src={src} />
            <p className="text-black">-</p>
          </span>
        ),
        'Order date': '-',
        Study: '-'
      }
    }
    return {
      Comments: isImagingOrderBundle(orderData)
        ? orderData.attributes.note || 'Nil'
        : orderData.attributes.notes || 'Nil',
      Radiology: isImagingOrderBundle(orderData) ? '-' : imaging?.attributes.category || '-',
      'Order by': (
        <span className="flex items-center gap-2">
          <Avatar size="24px" src={src} />
          <p className="text-black">
            {staffRow?.attributes?.first_name
              ? `${staffRow.meta?.roles?.includes('doctor') ? 'Dr. ' : ''}${staffRow.attributes.first_name} ${staffRow.attributes.last_name ?? ''}`.trim()
              : '-'}
          </p>
        </span>
      ),
      'Order date': orderData.attributes.created_at
        ? moment(orderData.attributes.created_at).format('DD/MM/YYYY')
        : '-',
      Study: isImagingOrderBundle(orderData)
        ? orderData.attributes.study_names?.join(', ') || '-'
        : imaging?.attributes.study_type || orderData.attributes.name || '-'
    }
  }, [imaging, orderData, staffRow])

  /**
   * Free-text-only orders complete only once the backend confirms every study has a file.
   * Orders with catalog lines complete through their child orders regardless.
   */
  const hasCatalogLines = placedLines.some(line => Boolean(line.orderId))
  const canCompleteOrder =
    !isImagingOrderBundle(orderData) || hasCatalogLines || orderData.attributes.can_complete_via_upload

  const toggleKey = (key: string, checked: boolean) => {
    setSelectedKeys(prev => {
      const next = new Set(prev)
      if (checked) next.add(key)
      else next.delete(key)
      return next
    })
  }

  const toggleAll = (checked: boolean) => {
    setSelectedKeys(checked ? new Set(lineKeys) : new Set())
  }

  const openCancelOrder = () => {
    // Legacy rows have no bundle to cancel — patch the single `order` row instead.
    openModal(isBundle ? DOCTOR_MODAL_TYPE.CANCEL_ALL_IMAGING_ORDERS : DOCTOR_MODAL_TYPE.CANCEL_IMAGING_ORDER, { id })
  }

  const handleCompleteOrder = useCallback(async () => {
    if (!id) return
    if (!guardEncounterWrite()) return
    setIsCompleting(true)
    try {
      if (isBundle) {
        await completeImagingOrder({ id })
      } else {
        await updateOrderStatus({ formVal: { status: 'completed' }, id })
      }
      showToast('Imaging order completed successfully')
    } catch (error: unknown) {
      const err = error as { response?: { data?: { errors?: { detail?: string }[] } } }
      showToast(err?.response?.data?.errors?.[0]?.detail || 'Failed to complete imaging order')
    } finally {
      setIsCompleting(false)
    }
  }, [completeImagingOrder, id, isBundle, updateOrderStatus])

  const handleOpenAttachment = useCallback(
    (attachment: ImagingResultAttachmentIncluded) => {
      dispatch(
        showViewImagingResultFile({
          defaultData: {
            byte_size: attachment.byte_size,
            content_type: attachment.content_type,
            filename: attachment.filename,
            url: resolveApiAssetUrl(attachment.url)
          },
          id: String(attachment.id)
        })
      )
    },
    [dispatch]
  )

  const handlePrintAttachment = useCallback((attachment: ImagingResultAttachmentIncluded) => {
    const resolved = resolveApiAssetUrl(attachment.url)
    if (!resolved) {
      showToast('No document available to print')
      return
    }
    const win = window.open(resolved, '_blank', 'noopener')
    win?.print()
  }, [])

  const handleUploadResult = useCallback(
    async (uploadedFile: File, studyName: string) => {
      if (!id) return
      if (!guardEncounterWrite()) return
      try {
        const { signed_id } = await uploadFile(uploadedFile)
        await createImagingOrderResult({
          formVal: createImagingResultAttachmentPayload(signed_id, studyName),
          imaging_order_id: id
        })
        showToast('File uploaded and imaging result created')
      } catch (err) {
        getError(err)
      }
    },
    [createImagingOrderResult, id, uploadFile]
  )

  const handleDeleteResult = useCallback(async (result: ResultEntry) => {
    setResultToDelete(result)
  }, [])

  const confirmDeleteResult = useCallback(async () => {
    if (!resultToDelete?.resultId) return
    try {
      await deleteImagingResult(resultToDelete.resultId)
      showToast('Imaging result deleted successfully')
      setResultToDelete(null)
    } catch (err) {
      getError(err)
    }
  }, [deleteImagingResult, resultToDelete])

  if (!isOpen) return null

  if (!orderData) {
    return (
      <SideDrawer onClose={onClose} title="Details">
        <Box align="center" justify="center" pad="large">
          {singleImagingOrderIsLoading ? <Loader /> : <Text color="#666">Unable to load this imaging order.</Text>}
        </Box>
      </SideDrawer>
    )
  }

  return (
    <SideDrawer onClose={onClose} title="Details">
      <div className="px-6 pt-2">
        <div className="mb-2 flex items-center justify-between">
          <Text color="black" size="22px" weight="bold">
            Order {orderTitleNumber(id)}
          </Text>
          <Badge
            justify="center"
            label={statusLabel === 'in_progress' ? 'In Progress' : statusLabel}
            size="small"
            status={statusLabel as keyof typeof OrderStatus}
          />
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {isStudyNamesEditable ? (
            <Button
              kinds="outline"
              label="Edit studies"
              onClick={() =>
                openModal(DOCTOR_MODAL_TYPE.EDIT_IMAGING_ORDER, {
                  defaultData: isBundle ? orderData : undefined,
                  id,
                  included
                })
              }
              rounded={false}
              size="sm"
              type="button"
            />
          ) : null}
          <Button
            disabled={isCompleting || isCompleteOrCancelled || !canCompleteOrder}
            icon={<PrintIcon />}
            kinds="outline"
            label={isCompleting ? 'Completing...' : 'Complete order'}
            onClick={handleCompleteOrder}
            rounded={false}
            size="sm"
            type="button"
          />
          {outsideLines.length > 0 ? (
            <Button
              icon={<PrintIcon />}
              kinds="outline"
              label="Print referral slip"
              onClick={handlePrintReferral}
              rounded={false}
              size="sm"
              type="button"
            />
          ) : null}
          <Button
            className="!rounded-[10px] bg-white !text-[#E34949] hover:bg-[#FFF5F5]"
            disabled={isCompleteOrCancelled}
            icon={<DeleteIcon alt />}
            kinds="outline"
            label="Cancel order"
            onClick={openCancelOrder}
            rounded={false}
            size="sm"
            type="button"
          />
        </div>
      </div>

      <ActiveTab
        divider
        key={id}
        options={[...TABS]}
        setActive={value => setActiveTab(value as (typeof TABS)[number])}
      />

      <div className="flex flex-col gap-4 px-6 pb-8 pt-4">
        {activeTab === 'Order Details' && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3">
              {Object.entries(detailRows).map(([key, value]) => (
                <div className="flex items-center justify-between gap-4" key={key}>
                  <p className="text-sm font-medium text-[#666666]">{key}:</p>
                  <div className="flex justify-end">
                    <Tip
                      content={value}
                      dropProps={{ style: { fontSize: '14px', lineHeight: '24px', maxWidth: '400px' } }}
                    >
                      <p className="max-w-[350px] truncate text-end text-black">{value}</p>
                    </Tip>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2">
              <p className="mb-2 text-base text-black">Orders placed</p>
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between py-3">
                  <CheckBox
                    checked={allSelected}
                    disabled={isCompleteOrCancelled}
                    label={<p className="text-sm">All studies</p>}
                    onChange={e => toggleAll(e.target.checked)}
                  />
                  {allSelected && (
                    <Button
                      className="!min-w-0 !rounded-lg border border-[#E34949] !bg-transparent !px-3 !py-1 !text-[13px] !font-medium !text-[#E34949]"
                      icon={<DeleteIcon alt />}
                      kinds="normal"
                      label="Cancel order"
                      onClick={openCancelOrder}
                      size="auto"
                      type="button"
                    />
                  )}
                </div>
                {placedLines.map(line => {
                  const cancelled = ['cancelled', 'canceled'].includes(line.lineStatus.toLowerCase())
                  const completed = line.lineStatus.toLowerCase() === 'completed'
                  const canCancelLine = Boolean(line.orderId)
                  return (
                    <div className="flex items-center justify-between py-1" key={line.key}>
                      <CheckBox
                        checked={selectedKeys.has(line.key)}
                        disabled={cancelled || completed || !canCancelLine}
                        label={
                          <p className="text-sm">
                            {line.label}
                            {line.performedAt === 'outside' ? (
                              <span className="ml-2 rounded bg-[#FFF4E5] px-1.5 py-0.5 text-[11px] font-medium text-[#B26A00]">
                                Outside
                              </span>
                            ) : null}
                          </p>
                        }
                        onChange={e => toggleKey(line.key, e.target.checked)}
                      />
                      {cancelled ? (
                        <Badge
                          label="Cancelled"
                          size="small"
                          status="cancelled"
                          style={{ background: '#FEECEC', color: '#E34949', minWidth: 'auto' }}
                        />
                      ) : (
                        <Button
                          className="!min-w-0 !rounded-lg border border-[#E34949] !bg-transparent !px-3 !py-1 !text-[13px] !font-medium !text-[#E34949]"
                          disabled={completed || cancelled || !canCancelLine}
                          icon={<DeleteIcon alt />}
                          kinds="normal"
                          label="Cancel"
                          onClick={() => {
                            if (!line.orderId) {
                              showToast('Free-text studies cannot be cancelled individually.')
                              return
                            }
                            openModal(DOCTOR_MODAL_TYPE.CANCEL_IMAGING_ORDER, { id: line.orderId })
                          }}
                          size="auto"
                          type="button"
                        />
                      )}
                    </div>
                  )
                })}
              </div>
              {isBundle && !isStudyNamesEditable && !isCompleteOrCancelled ? (
                <Text color="#909090" size="12px">
                  Studies are locked because a result has already been uploaded.
                </Text>
              ) : null}
            </div>
          </div>
        )}

        {activeTab === 'Result' && (
          <div className="flex flex-col gap-4">
            {isImagingResultLoading ? (
              <Box align="center" justify="center" pad="medium">
                <Loader />
              </Box>
            ) : (
              <>
                {(imagingResult?.data?.attributes.findings || imagingResult?.data?.attributes.interpretation) && (
                  <div className="flex flex-col gap-3">
                    {imagingResult.data.attributes.findings && (
                      <div>
                        <p className="mb-1 text-sm font-semibold text-black">Findings</p>
                        <div className="text-sm text-[#333]">{parse(imagingResult.data.attributes.findings)}</div>
                      </div>
                    )}
                    {imagingResult.data.attributes.interpretation && (
                      <div>
                        <p className="mb-1 text-sm font-semibold text-black">Interpretation</p>
                        <div className="text-sm text-[#333]">{parse(imagingResult.data.attributes.interpretation)}</div>
                      </div>
                    )}
                  </div>
                )}

                {resultEntries.length > 0 ? (
                  <div className="mb-2 flex flex-col gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#666]">Saved results</p>
                    {resultEntries.map(entry => (
                      <div
                        className="flex w-full items-center gap-2 rounded-md border border-[#e8e8e8] bg-white px-2 py-2 text-sm transition hover:border-[#ccc] hover:bg-[#fafafa]"
                        key={entry.key}
                      >
                        {entry.attachment && (
                          <img
                            alt="File Icon"
                            className="h-8 w-8"
                            src={getFileIconSrc(entry.attachment.content_type || entry.attachment.filename)}
                          />
                        )}
                        <button
                          className="min-w-0 flex-1 px-1 py-1 text-left"
                          onClick={() =>
                            entry.attachment
                              ? handleOpenAttachment(entry.attachment)
                              : showToast('No preview available for this result.')
                          }
                          type="button"
                        >
                          <span className="block truncate font-medium text-[#222]">{entry.label}</span>
                          {entry.attachment && (
                            <span className="block text-xs text-[#909090]">
                              {formatAttachmentSize(entry.attachment.byte_size)}
                            </span>
                          )}
                        </button>
                        {entry.attachment && (
                          <>
                            <button
                              className="shrink-0 rounded px-2 py-1 text-xs font-medium text-[#1565C0] hover:bg-[#E3F2FD]"
                              onClick={() => handlePrintAttachment(entry.attachment as ImagingResultAttachmentIncluded)}
                              type="button"
                            >
                              Print
                            </button>
                            <button
                              className="shrink-0 rounded px-2 py-1 text-xs text-[#666] hover:bg-[#f0f0f0]"
                              onClick={() => handleOpenAttachment(entry.attachment as ImagingResultAttachmentIncluded)}
                              type="button"
                            >
                              Preview
                            </button>
                            {entry.resultId && !isCompleteOrCancelled && (
                              <button
                                className="shrink-0 rounded px-2 py-1 text-xs font-medium text-[#E34949] hover:bg-[#FEECEC]"
                                disabled={isDeletingImagingResult}
                                onClick={() => handleDeleteResult(entry)}
                                type="button"
                              >
                                Delete
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <Text color="#909090" size="14px">
                    No results available.
                  </Text>
                )}

                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#666]">
                    Outside and other studies
                  </p>
                  {uploadableStudies.length > 0 && !isCompleteOrCancelled ? (
                    <div className="flex flex-col gap-3">
                      {uploadableStudies.map(line => (
                        <ResultUploader
                          isLoading={isUploading}
                          key={line.key}
                          label={line.performedAt === 'outside' ? `${line.label} (outside)` : line.label}
                          onFileUpload={file => handleUploadResult(file, line.label)}
                        />
                      ))}
                    </div>
                  ) : (
                    <Text color="#909090" size="14px">
                      No other tests.
                    </Text>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {resultToDelete && (
        <DeleteModal
          description={`Are you sure you want to delete "${resultToDelete.label}"? This action cannot be undone.`}
          handleSubmit={confirmDeleteResult}
          isLoading={isDeletingImagingResult}
          lBtnLabel="Cancel"
          onClose={() => setResultToDelete(null)}
          rBtnLabel="Yes, Delete"
          title="Delete Imaging Result"
        />
      )}

      {printReferral ? (
        <div className={`hidden print:block ${referralPrint.printRootClass}`} id="imaging-referral-print">
          <ReferralSlip
            facilityName={facilityName}
            kind="imaging"
            note={isImagingOrderBundle(orderData) ? orderData.attributes.note : orderData.attributes.notes}
            orderDate={orderData.attributes.created_at}
            orderNumber={orderTitleNumber(id)}
            orderedBy={orderedByLabel}
            patient={patientRow?.attributes}
            tests={placedLines}
          />
          <style>{referralPrint.css}</style>
        </div>
      ) : null}

      <ViewImagingResults />
    </SideDrawer>
  )
}

export default ImagingDetails
